/* eslint-disable no-restricted-syntax */
import type { Body, Meta, UIPluginOptions, Uppy } from '@uppy/core';
import { UIPlugin } from '@uppy/core';
import { h } from 'preact';
import { useRef, useState } from 'preact/hooks';
import './url-plugin.css';

interface UrlPluginOptions extends UIPluginOptions {
  companionUrl?: string;
  title?: string;
}

const KNOWN_EXTENSIONS = [
  'mp4',
  'mov',
  'avi',
  'mkv',
  'webm',
  'm4v',
  '3gp',
  'flv',
  'wmv',
  'ts',
  'mp3',
  'wav',
  'm4a',
  'aac',
  'ogg',
  'flac',
  'wma',
  'aiff',
  'txt',
  'docx',
  'pdf',
  'doc',
  'html',
  'srt',
  'vtt',
  'sbv',
  'ttml'
];

// Helper function to extract filename and infer extension from URL
const getFilenameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split('/').filter(s => s.length > 0);

    // Check segments in reverse — find the first one with a known extension
    for (let i = segments.length - 1; i >= 0; i--) {
      const decoded = decodeURIComponent(segments[i]);
      const ext = decoded.split('.').pop()?.toLowerCase();
      if (ext && KNOWN_EXTENSIONS.includes(ext)) {
        return decoded;
      }
    }

    // Fallback: use last segment or generic name
    const last = segments[segments.length - 1];
    return last ? decodeURIComponent(last) : 'URL Import';
  } catch {
    return 'URL Import';
  }
};

class UrlPlugin<M extends Meta, B extends Body> extends UIPlugin<
  UrlPluginOptions,
  M,
  B
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: () => any;

  constructor(uppy: Uppy<M, B>, opts?: UrlPluginOptions) {
    super(uppy, opts);
    this.id = this.opts.id || 'UrlPlugin';
    this.title = this.opts.title || 'Link';
    this.type = 'acquirer';
    this.icon = this.renderIcon;
  }

  renderIcon() {
    return h(
      'svg',
      {
        'aria-hidden': 'true',
        focusable: 'false',
        width: '18',
        height: '18',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      },
      h('path', {
        d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'
      }),
      h('path', {
        d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'
      })
    );
  }

  install() {
    const { target } = this.opts;
    if (target) {
      this.mount(target, this);
    }
  }

  uninstall() {
    this.unmount();
  }

  render() {
    return h(UrlForm, {
      uppy: this.uppy
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UrlForm = ({ uppy }: any) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [added, setAdded] = useState(false);
  // Guard against rapid re-clicks before the first re-render with disabled state
  const inflightRef = useRef(false);

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (inflightRef.current) return;

    setError('');
    setAdded(false);

    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    inflightRef.current = true;
    setSubmitting(true);

    try {
      const finalFilename = getFilenameFromUrl(url);

      uppy.addFile({
        name: finalFilename,
        type: 'application/octet-stream',
        data: new Blob([]),
        source: 'UrlPlugin',
        isRemote: true,
        remote: {
          companionUrl: '',
          url: url.trim(),
          body: { url: url.trim() }
        },
        meta: { url: url.trim() }
      });

      setUrl('');
      setSubmitting(false);
      setAdded(true);
      // Release the lock after the success window expires
      setTimeout(() => {
        setAdded(false);
        inflightRef.current = false;
      }, 1500);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err.message || 'Failed to add URL');
      setSubmitting(false);
      inflightRef.current = false;
    }
  };

  const isDisabled = submitting || added;

  return h(
    'div',
    { className: 'uppy-Url' },
    h(
      'div',
      { className: 'uppy-Url-container' },
      h(
        'form',
        { onSubmit: handleSubmit, className: 'uppy-Url-form' },
        h(
          'div',
          { className: 'uppy-Url-input-container' },
          h('input', {
            className: 'uppy-u-reset uppy-c-textInput uppy-Url-input',
            type: 'url',
            placeholder: 'https://example.com/video.mp4',
            value: url,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onInput: (e: any) => setUrl(e.target.value),
            'aria-label': 'Enter URL',
            disabled: isDisabled
          })
        ),
        error &&
          h(
            'div',
            {
              className: 'uppy-Url-error',
              style: { color: '#e32437', marginTop: '10px', fontSize: '14px' }
            },
            error
          ),
        added &&
          h(
            'div',
            {
              style: {
                color: '#1a9a32',
                marginTop: '10px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }
            },
            '✓ Added to queue'
          ),
        h(
          'button',
          {
            className:
              'uppy-u-reset uppy-c-btn uppy-c-btn-primary uppy-Url-submitButton',
            type: 'submit',
            disabled: isDisabled,
            'aria-disabled': isDisabled,
            style: { marginTop: '15px' }
          },
          added ? '✓ Added' : submitting ? 'Adding…' : 'Add URL'
        )
      )
    )
  );
};

export default UrlPlugin;
